import {
  TrendingDown,
  Clock,
  Users,
  Shield,
  Database,
  FileText,
  Code,
  GitBranch,
  Settings
} from "lucide-react";

export const benefits = [
  {
    icon: TrendingDown,
    title: "Slash Development Costs",
    subtitle: "Save 95% on Development Expenses",
    percentage: "95%",
    description: "Transform multi-million dollar projects into affordable innovations. What once required massive budgets now costs a fraction, making enterprise-grade development accessible to everyone.",
    metrics: [
      "9-month projects completed in days",
      "Million-dollar budgets reduced to thousands", 
      "Enterprise features at startup costs",
      "Instant ROI on development investments"
    ],
    color: "#00FF88",
    gradient: "from-green-400 to-emerald-600"
  },
  {
    icon: Clock,
    title: "Accelerate Time-to-Market",
    subtitle: "10x Faster Development Cycles",
    percentage: "10x",
    description: "Race ahead of competition with lightning-fast development. Complex backend systems that traditionally take months are now delivered in days, giving you the ultimate competitive advantage.",
    metrics: [
      "Quarterly releases become weekly sprints",
      "Prototype to production in hours",
      "Real-time feature implementation",
      "Instant scaling and deployment"
    ],
    color: "#00D9FF", 
    gradient: "from-cyan-400 to-blue-600"
  },
  {
    icon: Users,
    title: "Optimize Team Efficiency",
    subtitle: "Smaller, More Agile Teams",
    percentage: "5x",
    description: "Empower lean teams to achieve what once required armies of developers. Focus your talent on innovation and strategy while AI handles the heavy lifting of backend development.",
    metrics: [
      "2-3 developers replace 15+ person teams",
      "Enhanced productivity and job satisfaction",
      "Focus on creative problem-solving",
      "Reduced coordination complexity"
    ],
    color: "#8B5CF6",
    gradient: "from-purple-400 to-violet-600"
  },
  {
    icon: Shield,
    title: "Ensure Consistent Quality",
    subtitle: "Enterprise-Grade Standards",
    percentage: "100%",
    description: "Eliminate human error and inconsistency. Every project follows the same rigorous standards, ensuring bulletproof security, optimal performance, and seamless maintainability.",
    metrics: [
      "Standardized architecture patterns",
      "Built-in security best practices", 
      "Consistent code quality across projects",
      "Future-proof scalability design"
    ],
    color: "#F59E0B",
    gradient: "from-amber-400 to-orange-600"
  }
];

export const features = [
  {
    step: "01",
    title: "Generate Complex Architectures",
    subtitle: "Advanced System Design",
    videoSrc: "/videos/archi.webm",
    color: "#00D9FF",
    description: "ShredsAI creates sophisticated microservice architectures in real-time, designing scalable systems that handle enterprise-level complexity and scalability requirements.",
    highlights: [
      "Scalable microservices architecture",
      "Optimal system communication: brokers, events, etc.",
      "Services orchestration and load balancing",
      "External dependencies integration"
    ],
    icon: Database
  },
  {
    step: "02", 
    title: "Generate Detailed Specifications",
    subtitle: "Enterprise-Grade Fine-Grained Specs",
    videoSrc: "/videos/specs.webm",
    color: "#00FF88",
    description: "Generate comprehensive technical specifications progressively as you dive into services, with detailed requirements, system dependencies, and implementation guidelines.",
    highlights: [
      "Automated requirement analysis",
      "Technical specification generation",
      "Smart specs distributed among services",
      "Detailed down to exact library versions and business rules"
    ],
    icon: FileText
  },
  {
    step: "03",
    title: "Generate Production Code",
    subtitle: "Tested & Secured",
    videoSrc: "/videos/code.webm",
    color: "#FF6B35",
    description: "Experience state-of-the-art code generation with integration test coverage, creating production-ready implementations that follow industry best practices.",
    highlights: [
      "Production-ready code generation",
      "Comprehensive integration tests",
      "In-code documentation and explanations",
      "Hexagonal architecture for easy testing"
    ],
    icon: Code
  },
  {
    step: "04",
    title: "Automate Pull Requests",
    subtitle: "Quick & Precise PR Processing",
    videoSrc: "/videos/pr.webm",
    color: "#8B5CF6",
    description: "Benefit from automatic maintenance—just add a few comments in the PR and watch ShredsAI fix and test your software seamlessly.",
    highlights: [
      "Automated PR review processing",
      "Calculate modification impacts",
      "Test final code after modifications",
      "Quality assurance checks"
    ],
    icon: GitBranch
  },
  {
    step: "05",
    title: "Live Documentation Updates",
    subtitle: "Always Current Documentation",
    videoSrc: "/videos/docs.webm",
    color: "#F59E0B",
    description: "View real-time documentation that stays synchronized with your code, ensuring your team always has access to up-to-date information.",
    highlights: [
      "Real-time documentation sync",
      "Detailed technical explanations",
      "Diagrams & tables for quick understanding",
      "Evolves with your PRs"
    ],
    icon: Settings
  }
];